import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { AppContext } from '../containers/appContextContainer';
import { BilContainerWeb, BilUpdateWeb, SoeknadstypeWeb, ValgtWeb } from '../generated-types';
import errorTypes from '../util/gtm/errorTypes';
import restClient from '../util/web-http-client';

export interface DeleteBilParam {
    bilId: string;
    soeknadId: string;
}

export const useBil = (soeknadtype: SoeknadstypeWeb) => {
    const { setError } = useContext(AppContext);
    const query = useQuery({
        queryKey: ['bil'],
        queryFn: () => restClient.getBiler(soeknadtype),
        retry: 1,
    });

    useEffect(() => {
        if (query.error) {
            setError({ errorType: errorTypes.GET_BIL, errorException: query.error as RequestExceptionWithResponse });
        }
    }, [query.error, setError]);

    return query;
};

export const useUpdateBil = (bilContainer: BilContainerWeb | undefined, successCallback?: () => void) => {
    const { setError } = useContext(AppContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['updateBil'],
        mutationFn: (bilUpdateWeb: BilUpdateWeb) => restClient.updateBiler(bilUpdateWeb),
        onSuccess: (data) => {
            if (bilContainer) {
                bilContainer.biler = data;
                bilContainer.harBiler = data.length > 0 ? ValgtWeb.JA : ValgtWeb.NEI;
                queryClient.setQueryData(['bil'], bilContainer);
            }
            if (successCallback) {
                successCallback();
            }
        },
        onError: (error: unknown) => {
            setError({ errorType: errorTypes.UPDATE_BIL, errorException: error as RequestExceptionWithResponse });
        },
    });
};

export const useDeleteBil = () => {
    const { setError } = useContext(AppContext);
    return useMutation({
        mutationKey: ['deleteBil'],
        mutationFn: (deleteBilParam: DeleteBilParam) =>
            restClient.deleteBil(deleteBilParam.soeknadId, deleteBilParam.bilId),
        onError: (error: unknown) => {
            setError({ errorType: errorTypes.DELETE_BIL, errorException: error as RequestExceptionWithResponse });
        },
    });
};

export const useKjoeretoey = (soeknadtype: SoeknadstypeWeb) => {
    const { setError } = useContext(AppContext);
    const query = useQuery({
        queryKey: ['kjoeretoey'],
        queryFn: () => restClient.getKjoeretoeyAsync(soeknadtype),
        retry: 1,
    });

    useEffect(() => {
        if (query.error) {
            setError({
                errorType: errorTypes.GET_KJOERETOEY,
                errorException: query.error as RequestExceptionWithResponse,
            });
        }
    }, [query.error, setError]);

    return query;
};
